/**
 * @file charges.ts
 * @summary Librairie utilitaire
 *
 * Fonctions pures et helpers métier — pas de dépendance React. Couverture par tests unitaires Vitest dans `src/test/`.
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
// Available plates (kg per single plate), ordered heaviest first for tie-breaking
export const PLATES = [
  { kg: 20, label: "20 kg", color: "noir/bleu" },
  { kg: 15, label: "15 kg", color: "noir/jaune" },
  { kg: 10, label: "10 kg", color: "noir/vert" },
  { kg: 2.5, label: "2,5 kg", color: "petit rouge" },
  { kg: 1, label: "1 kg", color: "petit vert" },
];

export const PLATE_COLORS: Record<number, string> = {
  20: "bg-blue-700 text-white",
  15: "bg-yellow-500 text-black",
  10: "bg-green-600 text-white",
  2.5: "bg-red-500 text-white",
  1: "bg-emerald-400 text-black",
};

export interface PlateCount {
  kg: number;
  label: string;
  color: string;
  count: number;
}

export interface SuggestResult {
  suggestedKg: number;
  targetKg: number;
  deltaKg: number;
  perSidePlates: PlateCount[];
}

export const BARS = [
  { kg: 20, label: "Barre homme (20 kg)", accent: "border-blue-500", accentBg: "bg-blue-500/10", accentText: "text-blue-600", tag: "Homme" },
  { kg: 15, label: "Barre femme (15 kg)", accent: "border-yellow-500", accentBg: "bg-yellow-500/10", accentText: "text-yellow-600", tag: "Femme" },
  { kg: 10, label: "Barre débutant (10 kg)", accent: "border-gray-700", accentBg: "bg-gray-700/10", accentText: "text-gray-700", tag: "Débutant" },
];

// Work in half-kg units to avoid floating point issues
const plateUnits = PLATES.map((p) => ({ ...p, units: Math.round(p.kg / 0.5) }));
// Max units per side: 250kg total, bar 10 => 240/2 = 120 kg => 240 units
const MAX_UNITS = 260;

// DP: for each reachable unit sum, store minimum number of discs
// With tie-breaking: prefer heaviest plates first (lower index in plateUnits)
// dp[u] = min discs to make exactly u units, -1 if unreachable
// To implement heaviest-first tie-breaking, we process plates in order (heaviest first)
// and for equal disc count, prefer the solution that uses heavier plates.

const dp = new Int16Array(MAX_UNITS + 1).fill(-1);
const parentUnits = new Int16Array(MAX_UNITS + 1).fill(-1);
const coinUsed = new Int16Array(MAX_UNITS + 1).fill(-1);
dp[0] = 0;

for (let u = 1; u <= MAX_UNITS; u++) {
  let best = -1;
  let bestCoin = -1;
  // Iterate plates heaviest first (index 0 = 20kg, index 4 = 1kg)
  for (let i = 0; i < plateUnits.length; i++) {
    const pu = plateUnits[i].units;
    if (pu <= u && dp[u - pu] >= 0) {
      const candidate = dp[u - pu] + 1;
      if (best < 0 || candidate < best) {
        best = candidate;
        bestCoin = i;
      }
      // Same disc count but heavier plate (lower index) wins tie-break
      // Since we iterate heaviest first, the first match at `best` count already uses heaviest
      if (candidate === best && i < bestCoin) {
        bestCoin = i;
      }
    }
  }
  if (best >= 0) {
    dp[u] = best;
    parentUnits[u] = u - plateUnits[bestCoin].units;
    coinUsed[u] = bestCoin;
  }
}

function reconstructPlates(units: number): PlateCount[] {
  const counts = new Map<number, number>();
  let u = units;
  while (u > 0) {
    const ci = coinUsed[u];
    if (ci < 0) break;
    const p = plateUnits[ci];
    counts.set(p.kg, (counts.get(p.kg) || 0) + 1);
    u = parentUnits[u];
  }
  // Return in heaviest-first order (PLATES is already ordered that way)
  return PLATES.filter((p) => counts.has(p.kg)).map((p) => ({
    kg: p.kg,
    label: p.label,
    color: p.color,
    count: counts.get(p.kg)!,
  }));
}

export function suggestLoad(targetKg: number, barKg: number): SuggestResult {
  if (targetKg <= barKg) {
    return { suggestedKg: barKg, targetKg, deltaKg: Math.round((barKg - targetKg) * 100) / 100, perSidePlates: [] };
  }

  const need = targetKg - barKg;
  const needSide = need / 2;
  const needSideUnits = Math.ceil(needSide / 0.5);

  // Find smallest reachable units >= needSideUnits
  let found = -1;
  for (let u = needSideUnits; u <= MAX_UNITS; u++) {
    if (dp[u] >= 0) { found = u; break; }
  }

  if (found < 0) {
    return { suggestedKg: targetKg, targetKg, deltaKg: 0, perSidePlates: [] };
  }

  const perSideKg = found * 0.5;
  const suggestedKg = barKg + 2 * perSideKg;
  return {
    suggestedKg,
    targetKg,
    deltaKg: Math.round((suggestedKg - targetKg) * 100) / 100,
    perSidePlates: reconstructPlates(found),
  };
}

// 1RM formulas
export const FORMULAS = [
  {
    name: "Epley",
    fn: (w: number, r: number) => w * (1 + r / 30),
    eq: "1RM = w × (1 + reps/30)",
    description: "La plus utilisée et polyvalente. Fiable pour 3-10 reps, idéale pour les mouvements de force générale",
  },
  {
    name: "Brzycki",
    fn: (w: number, r: number) => w * 36 / (37 - r),
    eq: "1RM = w × 36 / (37 − reps)",
    description: "Très fiable pour les séries courtes (1-6 reps). Privilégiée pour les mouvements lents : squat, bench press, strict press",
  },
  {
    name: "Lombardi",
    fn: (w: number, r: number) => w * Math.pow(r, 0.10),
    eq: "1RM = w × reps^0.10",
    description: "Adaptée aux séries moyennes à longues (8-15 reps). Donne des résultats plus conservateurs",
  },
  {
    name: "O'Conner",
    fn: (w: number, r: number) => w * (1 + r / 40),
    eq: "1RM = w × (1 + reps/40)",
    description: "Plus prudente qu'Epley. Pertinente pour les mouvements explosifs et d'haltérophilie (clean, snatch, jerk)",
  },
];

// Per-movement recommended formula index
// 0=Epley, 1=Brzycki, 2=Lombardi, 3=O'Conner
export const MOVEMENT_DEFAULT_FORMULA: Record<string, number> = {
  "Back squat": 1,
  "Front squat": 1,
  "Deadlift": 1,
  "Bench press": 1,
  "Strict press": 1,
  "Push press": 3,
  "Push jerk": 3,
  "Split jerk": 3,
  "Clean": 3,
  "Snatch": 3,
  "Clean & jerk": 3,
  "Thruster": 0,
  "Autre": 0,
};

export const MOVEMENTS = [
  "Back squat", "Front squat", "Deadlift", "Bench press", "Strict press",
  "Push press", "Push jerk", "Split jerk", "Clean", "Snatch",
  "Clean & jerk", "Thruster", "Autre",
];

export const PCT_COLS = [40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 105, 110, 115, 120];
export const FIXED_COLS: number[] = [];
for (let i = 20; i <= 250; i += 10) FIXED_COLS.push(i);
